import { CacheAddOptions, CacheRecords } from './types';

export interface CacheOptions {
	enableLogging?: boolean;
}

/**
 * Cache Class is used to:
 *
 * 1. provide a caching mechanism
 */
export class Cache {
	#data: CacheRecords;
	#eventInvalidators: Record<
		// noun+|+action
		string,
		{
			ids: [string];
		}
	>;
	#enableLogging: boolean;
	#processingKeys: Set<string>;

	constructor (options: CacheOptions = {}) {
		this.#data = {};
		this.#eventInvalidators = {};
		this.#enableLogging = options.enableLogging ?? false;
		this.#processingKeys = new Set();
	}

	// eslint-disable-next-line max-params
	private log (action: string, key: string, message: string, ...args: any[]): void {
		if (this.#enableLogging) {
			console.log(`CACHE:${action} [${key}]`, message, ...args);
		}
	}

	/**
	 * adds an entry to the cache hash
	 * @param id key of cache item (string or array for composite keys)
	 * @param type used to help isolate and identify cached data type
	 * @param data data to cache
	 * @param options see CacheAddOptions
	 * @returns number of times the item was added to the cache
	 */
	// eslint-disable-next-line max-params
	public add<M> (id: string | Array<string>, dataType: string, data: M, options: CacheAddOptions = {}): number {
		const identifier = typeof id === 'string' ? id : id.join('|');
		const { overwrite = false, events = null, invalidate = null } = options;
		if (this.#data[identifier]) {
			this.log('ADD', identifier, 'already cached, incrementing count', this.#data[identifier].count);
			this.#data[identifier].count++;
			if (overwrite) {
				this.#data[identifier].data = data;
				this.log('ADD', identifier, 'overwrote cached data', dataType);
			}
		} else {
			this.log('ADD', identifier, 'new cache item', dataType, data);
			const invalidatorList = events && events.length > 0 ? events.map((e) => `${e[0]}|${e[1]}`) : null;
			this.#data[identifier] = {
				type: dataType,
				count: 1,
				fnInvalidate: invalidate,
				eventInvalidators: invalidatorList,
				data
			};

			// add event invalidators to the eventInvalidators hash
			if (invalidatorList) {
				this.log('ADD', identifier, 'adding event invalidators', invalidatorList);
				invalidatorList.forEach((e) => {
					if (this.#eventInvalidators[e]) this.#eventInvalidators[e].ids.push(identifier);
					else this.#eventInvalidators[e] = { ids: [identifier] };
					this.log('ADD', identifier, 'added event invalidator', e, this.#eventInvalidators[e]);
				});
			}
		}
		return this.#data[identifier].count;
	}

	/**
	 * delete an entry in the cache hash using the provided id
	 * @param id item to remove if no longer referenced (string or array for composite keys)
	 * @returns true if no longer used and entry is removed from the hash
	 */
	public delete (id: string | Array<string>): boolean {
		const identifier = typeof id === 'string' ? id : id.join('|');
		const cacheItem = this.#data[identifier];
		if (cacheItem) {
			cacheItem.count--;
			this.log('DELETE', identifier, 'decrement count', cacheItem.count);
		}

		const del = cacheItem ? cacheItem.count <= 0 : false;

		// remove event invalidators from the eventInvalidators hash
		this.#data[identifier]?.eventInvalidators?.forEach((e) => {
			if (this.#eventInvalidators[e]) {
				this.log('DELETE', identifier, 'removing event invalidator', e, this.#eventInvalidators[e]);
				// if event invalidator exists, remove identifier from list
				const idx = this.#eventInvalidators[e].ids.indexOf(identifier);
				if (idx > -1) this.#eventInvalidators[e].ids.splice(idx, 1);
			}
		});

		if (del) {
			this.log('DELETE', identifier, 'remove cache item', this.#data[identifier]);
			delete this.#data[identifier];
		}
		return del;
	}

	/**
	 * finds a matching cache item, returns if found
	 * @param id id to find
	 * @param callback functions that returns tuple [type, data], if provided will create a new caache entry
	 * @returns cached or callback item, otherwise null
	 */
	public match<M> (id: string | Array<string>, callback: () => [string, M] = null, options: CacheAddOptions = {}): M | null {
		const identifier = typeof id === 'string' ? id : id.join('|');
		const cacheItem = this.#data[identifier];
		if (cacheItem) {
			this.log('MATCH', identifier, 'cache hit', cacheItem.type);
			return cacheItem.data as M;
		}

		// Check for recursion - if this key is already being processed, return null to prevent infinite loop
		if (this.#processingKeys.has(identifier)) {
			this.log('MATCH', identifier, 'recursion detected, returning null to prevent infinite loop');
			return null;
		}

		if (callback) {
			this.log('MATCH', identifier, 'cache miss, using callback');

			// Mark this key as being processed BEFORE calling the callback
			this.#processingKeys.add(identifier);

			try {
				const [type, newCacheItem] = callback();
				this.log('MATCH', identifier, 'callback executed', type, newCacheItem);
				if (newCacheItem) {
					this.add<M>(identifier, type, newCacheItem, options);
					return newCacheItem;
				}
			} finally {
				// Always remove the key from processing set, even if callback throws
				this.#processingKeys.delete(identifier);
			}
		} else {
			this.log('MATCH', identifier, 'cache miss, no callback provided');
		}

		return null;
	}

	/**
	 * removes all entries and event invalidators from the cache
	 */
	public clear (): void {
		this.log('CLEAR', '*', 'clearing all cache items');
		this.#data = {};
		this.#eventInvalidators = {};
	}

	/**
	 * callback function for use with events to invalidate cache objects
	 * @param noun
	 * @param action
	 */
	public invalidateEvent (noun: string, action: string): void {
		const event = `${noun}|${action}`;
		if (this.#eventInvalidators[event]) {
			const idsToInvalidate = this.#eventInvalidators[event]?.ids || [];
			this.log('INVALIDATE_EVENT', event, 'invalidating cache items', idsToInvalidate);
			idsToInvalidate.forEach((id) => {
				// if event invalidator exists, remove id from list
				if (this.#data[id]) {
					this.log('INVALIDATE_EVENT', event, 'removing cache item', id, this.#data[id].type);
					delete this.#data[id];
				}
			});
			this.#eventInvalidators[event] = null;
		} else {
			this.log('INVALIDATE_EVENT', event, 'no cache items found for event');
		}
	}
}
